import { AppStore } from '../state/store';
import { PrintJob } from '../core/print/types';
import { generatePrintJob } from '../core/print/generate';
import { encodeBinaryStl } from '../core/print/stl';
import { buildPrintZip, partFileName } from '../core/print/zip';
import { FLAT_TERRAIN, generateSupports } from '../core/supports/generate';
import { flashHint } from './toolbar';
import { bindPanelCollapse, panelEnd, panelStart } from './collapsiblePanel';
import { setSidebarTab, sidebarPane } from './sidebarTabs';

export interface PrintPanelCallbacks {
  onJobChanged(job: PrintJob | null): void;
  onPreviewMode(show: boolean, kind: 'chunks' | 'parts'): void;
  onHighlightPart(partId: string | null): void;
  onPartVisible(partId: string, visible: boolean): void;
}

/** Printing settings, generate, part list, and downloads. */
export class PrintPanel {
  private el: HTMLElement;
  private job: PrintJob | null = null;
  private showPreview = false;
  private previewKind: 'chunks' | 'parts' = 'chunks';
  private status = '';
  private generating = false;
  /** 0..1 while generating; otherwise unused. */
  private progress = 0;
  private selectedPartId: string | null = null;
  /** Part ids unchecked in the list — hidden in the 3D preview. */
  private hiddenPartIds = new Set<string>();
  private logCopiedFlash = false;
  /** Free-text filter for the generation log (e.g. `006` or `track-006`). */
  private logFilter = '';

  constructor(
    private store: AppStore,
    private cb: PrintPanelCallbacks,
  ) {
    this.el = document.createElement('div');
    sidebarPane('print').appendChild(this.el);
    store.on('doc', () => {
      this.job = null;
      this.selectedPartId = null;
      this.hiddenPartIds.clear();
      this.cb.onJobChanged(null);
      this.render();
    });
    store.on('track', () => {
      this.job = null;
      this.selectedPartId = null;
      this.hiddenPartIds.clear();
      this.cb.onJobChanged(null);
      this.render();
    });
    store.on('supports', () => {
      this.job = null;
      this.selectedPartId = null;
      this.hiddenPartIds.clear();
      this.cb.onJobChanged(null);
      this.render();
    });
    this.render();
  }

  /** Select a part from the 3D view (or list) and sync highlight + scroll. */
  selectPart(partId: string | null, opts?: { scroll?: boolean }): void {
    this.selectedPartId = partId;
    this.cb.onHighlightPart(partId);
    this.syncPartListSelection(opts?.scroll === true);
    if (partId) setSidebarTab('print', { expand: true });
  }

  /** Toggle 3D visibility for the selected part (hotkey H). Returns true if toggled. */
  toggleSelectedPartVisibility(): boolean {
    if (!this.job || !this.selectedPartId) return false;
    const partId = this.selectedPartId;
    const nowVisible = this.hiddenPartIds.has(partId);
    if (nowVisible) this.hiddenPartIds.delete(partId);
    else this.hiddenPartIds.add(partId);
    this.cb.onPartVisible(partId, nowVisible);
    this.syncPartVisibleCheckbox(partId, nowVisible);
    return true;
  }

  private syncPartListSelection(scroll: boolean): void {
    this.el.querySelectorAll<HTMLElement>('[data-part-row]').forEach((row) => {
      const on = row.dataset.partRow === this.selectedPartId;
      row.classList.toggle('is-selected', on);
      if (on && scroll) {
        row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }

  private syncPartVisibleCheckbox(partId: string, visible: boolean): void {
    this.el.querySelectorAll<HTMLInputElement>('input[data-action="partVisible"]').forEach((input) => {
      if (input.dataset.part === partId) input.checked = visible;
    });
  }

  private render(): void {
    const print = this.store.doc.print;
    const canDownload = !!this.job && this.job.parts.length > 0;

    this.el.innerHTML = `
      ${panelStart('printing', 'Printing')}
        <div class="row">
          <label>Chunk size (mm)
            <input type="range" data-key="chunkSizeMm" min="80" max="350" step="10"
              value="${print.chunkSizeMm}">
            <span class="val">${print.chunkSizeMm}</span>
          </label>
        </div>
        <div class="row">
          <label>Cut clearance from supports (mm)
            <input type="range" data-key="supportCutClearanceMm" min="0" max="50" step="1"
              value="${print.supportCutClearanceMm}">
            <span class="val">${print.supportCutClearanceMm}</span>
          </label>
        </div>
        <div class="row">
          <label><input type="checkbox" data-action="separateSupport"
            ${print.separateSupport ? 'checked' : ''}> SeparateSupport</label>
        </div>
        <div class="row">
          <label><input type="checkbox" data-action="splitAtToothBottom"
            ${print.splitAtToothBottom ? 'checked' : ''}> Split at nearest tooth bottom</label>
        </div>
        <div class="row">
          <button type="button" data-action="generate" ${this.generating ? 'disabled' : ''}>
            ${this.generating ? 'Generating…' : 'Generate'}
          </button>
          <button type="button" data-action="download-all" ${canDownload ? '' : 'disabled'}>
            Download full print model
          </button>
        </div>
        <div class="row print-preview-row">
          <label><input type="checkbox" data-action="showPreview"
            ${this.showPreview ? 'checked' : ''}> Show</label>
          <label><input type="radio" name="printPreviewKind" data-preview-kind="chunks"
            ${this.previewKind === 'chunks' ? 'checked' : ''}> Chunks</label>
          <label><input type="radio" name="printPreviewKind" data-preview-kind="parts"
            ${this.previewKind === 'parts' ? 'checked' : ''}> Parts</label>
        </div>
        ${
          this.generating
            ? `<div class="print-progress" data-print-progress>
                <div class="print-progress-bar">
                  <div class="print-progress-fill" style="width:${Math.round(this.progress * 100)}%"></div>
                </div>
                <div class="print-progress-meta">
                  <span class="print-progress-label">${escapeHtml(this.status || 'Generating…')}</span>
                  <span class="print-progress-pct">${Math.round(this.progress * 100)}%</span>
                </div>
              </div>`
            : this.status
              ? `<p class="hint">${escapeHtml(this.status)}</p>`
              : ''
        }
        ${this.job ? partsListHtml(this.job, this.selectedPartId, this.hiddenPartIds) : '<p class="hint">Generate to list print parts.</p>'}
        ${this.job ? printLogHtml(this.job, this.logCopiedFlash, this.logFilter) : ''}
      ${panelEnd()}`;

    bindPanelCollapse(this.el);

    const logFilterInput = this.el.querySelector<HTMLInputElement>('[data-log-filter]');
    if (logFilterInput) {
      logFilterInput.addEventListener('input', () => {
        this.logFilter = logFilterInput.value;
        this.refreshLogView();
      });
      logFilterInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.logFilter = '';
          logFilterInput.value = '';
          this.refreshLogView();
        }
      });
    }

    this.el.querySelectorAll<HTMLInputElement>('input[type="range"]').forEach((range) => {
      const valEl = range.parentElement!.querySelector('.val');
      if (valEl) {
        range.addEventListener('input', () => {
          valEl.textContent = range.value;
        });
      }
      range.addEventListener('change', () => {
        const key = range.dataset.key;
        const value = parseFloat(range.value);
        if (key !== 'chunkSizeMm' && key !== 'supportCutClearanceMm') return;
        this.store.commit((d) => {
          if (key === 'chunkSizeMm') d.print.chunkSizeMm = value;
          else if (key === 'supportCutClearanceMm') d.print.supportCutClearanceMm = value;
        });
        this.job = null;
        this.selectedPartId = null;
        this.hiddenPartIds.clear();
        this.cb.onJobChanged(null);
        this.status = 'Settings changed — generate again.';
        this.render();
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((input) => {
      input.addEventListener('change', () => {
        const action = input.dataset.action;
        if (action === 'separateSupport' || action === 'splitAtToothBottom') {
          this.store.commit((d) => {
            if (action === 'separateSupport') d.print.separateSupport = input.checked;
            else d.print.splitAtToothBottom = input.checked;
          });
          this.job = null;
          this.selectedPartId = null;
          this.hiddenPartIds.clear();
          this.cb.onJobChanged(null);
          this.status = 'Settings changed — generate again.';
          this.render();
        } else if (action === 'showPreview') {
          this.showPreview = input.checked;
          this.cb.onPreviewMode(this.showPreview, this.previewKind);
          if (!this.showPreview) {
            this.selectedPartId = null;
            this.cb.onHighlightPart(null);
            this.syncPartListSelection(false);
          }
        } else if (action === 'partVisible') {
          const partId = input.dataset.part!;
          if (input.checked) this.hiddenPartIds.delete(partId);
          else this.hiddenPartIds.add(partId);
          this.cb.onPartVisible(partId, input.checked);
        }
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('input[name="printPreviewKind"]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        this.previewKind = input.dataset.previewKind === 'parts' ? 'parts' : 'chunks';
        this.showPreview = true;
        const showBox = this.el.querySelector<HTMLInputElement>('input[data-action="showPreview"]');
        if (showBox) showBox.checked = true;
        this.cb.onPreviewMode(true, this.previewKind);
      });
    });

    this.el.querySelectorAll<HTMLButtonElement>('button[data-action]').forEach((btn) => {
      btn.addEventListener('click', () => this.onAction(btn.dataset.action!));
    });

    this.el.querySelectorAll<HTMLElement>('[data-part-row]').forEach((row) => {
      const id = row.dataset.partRow!;
      row.addEventListener('mouseenter', () => this.cb.onHighlightPart(id));
      row.addEventListener('mouseleave', () => this.cb.onHighlightPart(this.selectedPartId));
      row.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('button, input, label')) return;
        this.selectPart(id, { scroll: false });
        if (id.startsWith('track-') || id.startsWith('support-')) {
          this.logFilter = id;
          this.refreshLogView(true);
        }
      });
    });

    this.el.querySelectorAll<HTMLButtonElement>('button[data-download-part]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.downloadPart(btn.dataset.downloadPart!);
      });
    });

    if (this.selectedPartId) this.syncPartListSelection(false);
  }

  private onAction(action: string): void {
    if (action === 'generate') this.generate();
    else if (action === 'download-all') this.downloadAll();
    else if (action === 'copy-log') void this.copyLog();
  }

  private filteredLogLines(): string[] {
    return filterPrintLog(this.job?.log ?? [], this.logFilter);
  }

  /** Update log textarea + meta without rebuilding the whole panel (keeps filter focus). */
  private refreshLogView(syncFilterInput = false): void {
    const ta = this.el.querySelector<HTMLTextAreaElement>('[data-print-log]');
    const meta = this.el.querySelector<HTMLElement>('[data-log-meta]');
    const filterInput = this.el.querySelector<HTMLInputElement>('[data-log-filter]');
    const lines = this.filteredLogLines();
    const total = this.job?.log?.length ?? 0;
    if (ta) ta.value = lines.join('\n');
    if (meta) {
      meta.textContent =
        this.logFilter.trim().length > 0
          ? `${lines.length} of ${total} lines`
          : `${total} lines`;
    }
    if (syncFilterInput && filterInput && filterInput.value !== this.logFilter) {
      filterInput.value = this.logFilter;
    }
    const copyBtn = this.el.querySelector<HTMLButtonElement>('[data-action="copy-log"]');
    if (copyBtn && !this.logCopiedFlash) {
      copyBtn.textContent = this.logFilter.trim() ? 'Copy filtered' : 'Copy log';
    }
  }

  private async copyLog(): Promise<void> {
    const text = this.filteredLogLines().join('\n');
    if (!text) {
      flashHint(this.logFilter.trim() ? 'No log lines match the filter.' : 'No print log yet — generate first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      this.logCopiedFlash = true;
      flashHint(this.logFilter.trim() ? 'Filtered print log copied' : 'Print log copied');
      const copyBtn = this.el.querySelector<HTMLButtonElement>('[data-action="copy-log"]');
      if (copyBtn) copyBtn.textContent = 'Copied';
      setTimeout(() => {
        this.logCopiedFlash = false;
        this.refreshLogView();
      }, 1500);
    } catch {
      // Fallback for clipboard permission denial: select the textarea.
      const ta = this.el.querySelector<HTMLTextAreaElement>('[data-print-log]');
      if (ta) {
        ta.focus();
        ta.select();
        flashHint('Select-all the log and copy (Ctrl+C)');
      } else {
        flashHint('Could not copy log');
      }
    }
  }

  /** Update progress bar + label without a full panel re-render. */
  private setProgress(fraction: number, message: string): void {
    this.progress = Math.min(1, Math.max(0, fraction));
    this.status = message;
    const root = this.el.querySelector<HTMLElement>('[data-print-progress]');
    if (!root) {
      this.render();
      return;
    }
    const fill = root.querySelector<HTMLElement>('.print-progress-fill');
    const label = root.querySelector<HTMLElement>('.print-progress-label');
    const pct = root.querySelector<HTMLElement>('.print-progress-pct');
    const pctText = `${Math.round(this.progress * 100)}%`;
    if (fill) fill.style.width = pctText;
    if (label) label.textContent = message;
    if (pct) pct.textContent = pctText;
  }

  private generate(): void {
    if (this.generating) return;
    this.generating = true;
    this.progress = 0;
    this.selectedPartId = null;
    this.hiddenPartIds.clear();
    this.logCopiedFlash = false;
    this.logFilter = '';
    this.status = 'Starting…';
    this.render();

    // Yield so the progress UI can paint before heavy work.
    setTimeout(async () => {
      try {
        const doc = this.store.doc;
        this.setProgress(0.01, 'Preparing supports…');
        await new Promise<void>((r) => setTimeout(r, 0));
        const supportPrimitives =
          doc.supportsEnabled === false
            ? []
            : generateSupports(
                this.store.runtime,
                FLAT_TERRAIN,
                doc.supports,
                doc.supportOverrides,
              ).primitives;

        this.job = await generatePrintJob(this.store.runtime, doc.print, {
          supportsEnabled: doc.supportsEnabled,
          supportParams: doc.supports,
          supportOverrides: doc.supportOverrides,
          supportPrimitives,
          trackShape: doc.trackShape,
          onProgress: (p) => this.setProgress(p.fraction, p.message),
        });
        this.cb.onJobChanged(this.job);
        const nTrack = this.job.parts.filter((p) => p.kind === 'track').length;
        const nSupport = this.job.parts.filter((p) => p.kind === 'support').length;
        const nFixed = this.job.parts.filter((p) => p.fixed).length;
        this.status = `Generated ${this.job.parts.length} parts (${nTrack} track, ${nSupport} support, ${nFixed} solidified).`;
        if (this.job.warnings.length) {
          this.status += ` ${this.job.warnings.length} warning(s).`;
        }
        flashHint(this.status);
      } catch (err) {
        this.job = null;
        this.cb.onJobChanged(null);
        this.status = `Generate failed: ${err instanceof Error ? err.message : err}`;
        flashHint(this.status);
      } finally {
        this.generating = false;
        this.progress = 0;
        this.render();
      }
    }, 20);
  }

  private downloadAll(): void {
    if (!this.job || this.job.parts.length === 0) return;
    const zip = buildPrintZip(this.job, this.store.doc.meta.name);
    downloadBlob(new Blob([toBlobPart(zip)], { type: 'application/zip' }), `${this.slug()}-print.zip`);
    flashHint('Downloaded print ZIP');
  }

  private downloadPart(partId: string): void {
    const part = this.job?.parts.find((p) => p.id === partId);
    if (!part) return;
    const stl = encodeBinaryStl(part.mesh, part.id);
    downloadBlob(new Blob([toBlobPart(stl)], { type: 'model/stl' }), `${this.slug()}-${partFileName(part)}`);
    flashHint(`Downloaded ${part.id}`);
  }

  private slug(): string {
    return (
      this.store.doc.meta.name
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64) || 'untitled'
    );
  }
}

function partsListHtml(
  job: PrintJob,
  selectedPartId: string | null,
  hiddenPartIds: Set<string>,
): string {
  if (job.parts.length === 0) return '<p class="hint">No parts.</p>';
  const rows = job.parts
    .map((p) => {
      const size = `${Math.round(p.obbMm.x)}×${Math.round(p.obbMm.y)}×${Math.round(p.obbMm.z)} mm`;
      const flags: string[] = [];
      if (p.oversized) flags.push('oversized');
      if (!p.fixed) flags.push('not solid');
      const flag =
        flags.length > 0 ? ` <span class="hint">(${flags.join(', ')})</span>` : '';
      const sel = p.id === selectedPartId ? ' is-selected' : '';
      const shown = !hiddenPartIds.has(p.id);
      return `<div class="print-part-row${sel}" data-part-row="${escapeAttr(p.id)}"
        style="display:flex;align-items:center;gap:6px;padding:3px 0;cursor:pointer">
        <label style="display:flex;margin:0;cursor:pointer" title="Show in preview">
          <input type="checkbox" data-action="partVisible" data-part="${escapeAttr(p.id)}"
            ${shown ? 'checked' : ''}>
        </label>
        <div style="flex:1;font-size:12px">${escapeHtml(p.id)} · ${escapeHtml(p.kind)} · ${escapeHtml(size)}${flag}</div>
        <button type="button" data-download-part="${escapeAttr(p.id)}" title="Download STL">⬇</button>
      </div>`;
    })
    .join('');
  return `<h4 style="margin:10px 0 4px;font-size:12px;color:#9aa7b5;font-weight:600">Parts</h4>
    <div class="hotkeys">
      <div class="hotkey-row"><kbd>H</kbd><span>Hide or show selected part in 3D</span></div>
    </div>
    <div class="file-list print-parts-list">${rows}</div>`;
}

function printLogHtml(job: PrintJob, copied: boolean, filter: string): string {
  const all = job.log ?? [];
  if (all.length === 0) return '';
  const lines = filterPrintLog(all, filter);
  const filtered = filter.trim().length > 0;
  const warnNote =
    job.warnings.length > 0
      ? `<p class="hint">${job.warnings.length} warning(s) — see log.</p>`
      : '';
  const meta = filtered ? `${lines.length} of ${all.length} lines` : `${all.length} lines`;
  return `<div class="print-log-block">
      <div class="print-log-head">
        <h4>Generation log</h4>
        <button type="button" data-action="copy-log">${copied ? 'Copied' : filtered ? 'Copy filtered' : 'Copy log'}</button>
      </div>
      <div class="print-log-filter-row">
        <input type="search" data-log-filter placeholder="Filter… e.g. 006 or track-006"
          value="${escapeAttr(filter)}" spellcheck="false">
        <span class="hint" data-log-meta>${escapeHtml(meta)}</span>
      </div>
      ${warnNote}
      <textarea class="print-log" data-print-log readonly rows="10" spellcheck="false">${escapeHtml(lines.join('\n'))}</textarea>
    </div>`;
}

/**
 * Filter generation log lines. Bare numbers / `track 006` / `track-006` match
 * that track part's chunk, solidify, and warning lines.
 */
export function filterPrintLog(lines: string[], query: string): string[] {
  const raw = query.trim();
  if (!raw) return lines;
  const q = raw.toLowerCase();

  const trackMatch = q.match(/^(?:track[-\s]*)?0*(\d+)$/);
  if (trackMatch) {
    const n = parseInt(trackMatch[1], 10);
    if (!Number.isFinite(n) || n < 1) {
      return lines.filter((line) => line.toLowerCase().includes(q));
    }
    const padded = String(n).padStart(3, '0');
    const patterns = [
      new RegExp(`\\btrack-${padded}\\b`, 'i'),
      new RegExp(`\\btrack\\s+${padded}\\b`, 'i'),
      new RegExp(`\\btrack\\s+part\\s+${padded}\\b`, 'i'),
      new RegExp(`\\btrack\\s+chunk\\s+${n}\\b`, 'i'),
      new RegExp(`\\btrack\\s+chunk\\s+${padded}\\b`, 'i'),
    ];
    return lines.filter((line) => patterns.some((re) => re.test(line)));
  }

  const supportMatch = q.match(/^(?:support[-\s]*)?0*(\d+)$/);
  if (supportMatch && /support/i.test(q)) {
    const n = parseInt(supportMatch[1], 10);
    const padded = String(n).padStart(3, '0');
    const patterns = [
      new RegExp(`\\bsupport-${padded}\\b`, 'i'),
      new RegExp(`\\bsupport\\s+${padded}\\b`, 'i'),
      new RegExp(`\\bsupport\\s+part\\s+${padded}\\b`, 'i'),
    ];
    return lines.filter((line) => patterns.some((re) => re.test(line)));
  }

  return lines.filter((line) => line.toLowerCase().includes(q));
}

function downloadBlob(blob: Blob, filename: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function toBlobPart(bytes: Uint8Array): BlobPart {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
